<script>
import utilsMixin from '@shared/mixins/utils';

const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'cancel_at_period_end', 'past_due'];

export default {
  mixins: [utilsMixin],
  props: {
    customer: {
      type: Object,
      default: null,
    },
    subscription: {
      type: Object,
      required: true,
    },
  },
  computed: {
    activePrices() {
      return this.subscription.prices || [];
    },
    defaultPrice() {
      return this.activePrices[0] || null;
    },
    subscriptionItems() {
      return (this.subscription.items || []).filter(Boolean);
    },
    visibleItems() {
      return this.subscriptionItems.slice(0, 3);
    },
    additionalItemsCount() {
      const extraCount = this.subscriptionItems.length - this.visibleItems.length;
      return extraCount > 0 ? extraCount : 0;
    },
    customerMemberships() {
      return this.$store?.getters?.['auth/customerMemberships'] || [];
    },
    isAlreadySubscribed() {
      if (!this.subscription) {
        return false;
      }

      const membershipUuid = this.subscription.uuid;
      const membershipId = this.subscription.id;

      return this.customerMemberships.some((subscription) => {
        if (!subscription || !ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)) {
          return false;
        }

        const subscriptionMembership = subscription.membership || {};

        if (membershipUuid && subscriptionMembership?.uuid === membershipUuid) {
          return true;
        }

        if (membershipId && subscription.membership_id) {
          return String(subscription.membership_id) === String(membershipId);
        }

        return false;
      });
    },
    buttonText() {
      if (this.isAlreadySubscribed) {
        return this.$t('access');
      }
      if (!this.defaultPrice) {
        return this.$t('no_prices_available');
      }
      return this.$t('subscribe');
    },
  },
  methods: {
    subscribe() {
      if (this.isAlreadySubscribed) {
        this.$router.push({ name: 'dashboard' });
        return;
      }

      const price = this.defaultPrice;

      if (!price) {
        if (this.$buefy?.toast) {
          this.$buefy.toast.open({
            message: this.$t('no_prices_available'),
            type: 'is-danger',
          });
        }
        return;
      }

      // Use the exact same flow as Training/Bundle purchase - triggers RegisterForm modal with 3D Secure support
      this.$router.push({
        query: {
          ...this.$route.query,
          acheter: 1,
          price: price.uuid,
        },
      });

      // Add analytics tracking like Training/Bundle
      if (this.$gtag) {
        this.$gtag.addToCart({
          items: [{
            id: this.subscription.uuid,
            name: this.subscription.name,
            quantity: 1,
            value: this.getEffectivePrice(price),
          }],
        });
      }

      if (this.$fbq) {
        this.$fbq.addToCart({
          content_ids: [this.subscription.uuid],
          content_name: this.subscription.name,
          value: this.getEffectivePrice(price),
        });
      }
    },
    hasDiscountPrice(price) {
      return price.discount_price && price.discount_price > 0 && price.discount_price < price.amount;
    },
    getEffectivePrice(price) {
      return this.hasDiscountPrice(price) ? price.discount_price : price.amount;
    },
    getItemLabel(item) {
      if (item && item.buyable && item.buyable.name) {
        return item.buyable.name;
      }
      return this.$t('default_feature_label');
    },
    theme() {
      return this.$t('theme');
    },
  },
  i18n: {
    messages: {
      fr: {
        theme: 'Thème',
        subscribe: 'S\'abonner',
        access: 'Accéder',
        login_to_subscribe: 'Se connecter pour s\'abonner',
        per_month: 'par mois',
        per_year: 'par an',
        no_prices_available: 'Aucun prix disponible',
        includes_title: 'Inclus dans cet abonnement :',
        default_feature_label: 'Contenu inclus',
        additional_items: 'Et {count} autre contenu|Et {count} autres contenus',
      },
      en: {
        theme: 'Theme',
        subscribe: 'Subscribe',
        access: 'Access',
        login_to_subscribe: 'Login to Subscribe',
        per_month: 'per month',
        per_year: 'per year',
        no_prices_available: 'No prices available',
        includes_title: 'Included in this subscription:',
        default_feature_label: 'Included item',
        additional_items: 'And {count} additional item|And {count} additional items',
      },
    },
  },
};
</script>

<template>
  <div class="subscription-buy-button">
    <!-- Subscribe Button -->
    <b-button
      @click="subscribe"
      type="is-primary is-custom"
      size="is-large"
      expanded
      :disabled="!isAlreadySubscribed && !defaultPrice">
      {{ buttonText }}
    </b-button>

    <!-- Features list for subscription -->
    <div class="mt-4" v-if="subscriptionItems.length">
      <p class="has-text-weight-semibold mb-2">{{ $t('includes_title') }}</p>
      <ul class="subscription-features">
        <li v-for="item in visibleItems" :key="item.uuid" class="is-size-7 has-text-grey-dark">
          <b-icon icon="check" size="is-small" type="is-success" class="mr-1" />
          {{ getItemLabel(item) }}
        </li>
        <li v-if="additionalItemsCount" class="is-size-7 has-text-grey-dark">
          <b-icon icon="check" size="is-small" type="is-success" class="mr-1" />
          {{ $tc('additional_items', additionalItemsCount, { count: additionalItemsCount }) }}
        </li>
      </ul>
    </div>

  </div>
</template>

<style scoped>
.subscription-buy-button {
  text-align: center;
}

.subscription-features {
  list-style: none;
  padding: 0;
  text-align: left;
}

.subscription-features li {
  padding: 0.25rem 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
}

.subscription-features-section {
  text-align: left;
  margin-left: 0;
}

</style>
